import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { List } from "immutable";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import ErrorBoundary from "~/components/ErrorBoundry";
import RecurringTaskForm from "~/components/tasks/CreateRecurringTaskModal/RecurringTaskForm";
import { makeCompaniesByProjectMembersSelector } from "~/store/selectors/selector.companies.jsx";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { makeTaskLabelsByProjectSelector } from "~/store/selectors/selector.labels.jsx";
import { getProjectById } from "~/store/selectors/selector.projects";
import { getRecurringTaskById } from "~/store/selectors/selector.recurringTasks";
import {
  getProjectSubscribers,
  getSortedSubscribersByProjectId,
  getSubscriberIdsByTypeAndId,
} from "~/store/selectors/selector.subscription";
import { makeTaskListsByProjectSelector } from "~/store/selectors/selector.taskLists.jsx";
import { makeTeamsByProjectMembersSelector } from "~/store/selectors/selector.teams.jsx";
import {
  canSeeTimeEstimates,
  isLoggedUserClientSelector,
  getLoggedUserId,
  getUsersFromProjectMembersForTaskUserSelector,
} from "~/store/selectors/selector.users.jsx";

const RecurringTaskFormContainer = ({ open, id, projectId, onClose }) => {
  const loggedUserId = useSelector(getLoggedUserId);

  const subscribersIds = useSelector((state) => getSubscriberIdsByTypeAndId(state, "RecurringTask", id));

  const getTaskListsByProjectSelector = useMemo(makeTaskListsByProjectSelector, []);
  const getTeamsByProjectMembersSelector = useMemo(makeTeamsByProjectMembersSelector, []);
  const getCompaniesByProjectMembersSelector = useMemo(
    () => makeCompaniesByProjectMembersSelector(projectId),
    [projectId]
  );
  const getTaskLabelsByProjectSelector = useMemo(makeTaskLabelsByProjectSelector, []);
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));
  const defaultHideFromClients = useSelector((state) => getRawConfigOption(state, "default_hide_from_clients"));
  const recurringTask = useSelector((state) => getRecurringTaskById(state, id ? id : 0));
  const projectMembers = useSelector((state) => getSortedSubscribersByProjectId(state, projectId));
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const initialProjectSubscriberIds = useSelector((state) => {
    return getProjectSubscribers(state, projectId);
  });

  const taskLists = useSelector((state) => getTaskListsByProjectSelector(state, projectId));

  const project = useSelector((state) => getProjectById(state, projectId).toJS());

  let childData = {};
  childData.users = useSelector((state) => getUsersFromProjectMembersForTaskUserSelector(state, projectId));

  // return teams, companies filtered by projectMembers
  childData.teams = useSelector((state) => getTeamsByProjectMembersSelector(state, projectId));
  childData.companies = useSelector(getCompaniesByProjectMembersSelector);
  childData.subscribers = recurringTask?.id ? (subscribersIds ? subscribersIds : [loggedUserId]) : [loggedUserId];
  childData.sortedSubscribers = projectMembers;
  childData.canSeeTimeEstimates = useSelector((state) => canSeeTimeEstimates(state, projectId));
  childData.name = recurringTask?.id ? recurringTask?.name : "";
  childData.description = recurringTask?.id ? recurringTask?.body : "";
  childData.bodyMode = recurringTask?.id && recurringTask?.body_mode ? recurringTask?.body_mode : "paragraph";
  childData.attachments = recurringTask?.id ? List(recurringTask?.attachments) : List([]);
  childData.assigneeId = recurringTask?.id ? +recurringTask?.assignee_id : null;
  childData.startOn = recurringTask?.id ? recurringTask?.start_in : "";
  childData.dueOn = recurringTask?.id ? recurringTask?.due_in : "";
  childData.labelIds = recurringTask?.id ? List(recurringTask.labels) : List([]);
  childData.estimate = recurringTask?.id ? `${recurringTask?.estimate}` : "";
  childData.jobTypeId = recurringTask?.id ? recurringTask?.job_type_id : null;
  childData.hiddenFromClients = !recurringTask?.id ? defaultHideFromClients : recurringTask?.is_hidden_from_clients;
  childData.important = recurringTask?.id ? recurringTask?.is_important : false;
  childData.hideFromClients = hideFromClients;
  childData.isLoggedUserClient = isLoggedUserClient;
  childData.taskLists = taskLists;
  childData.taskListId = recurringTask?.id ? recurringTask?.task_list_id : taskLists?.first()?.id;
  childData.repeatFrequency = recurringTask?.id ? recurringTask?.repeat_frequency : "never";
  childData.repeatAmount = recurringTask?.id ? recurringTask?.repeat_amount : 0;
  childData.repeatAmountExtended = recurringTask?.id ? recurringTask?.repeat_amount_extended : 0;

  childData.labels = useSelector((state) => getTaskLabelsByProjectSelector(state, projectId));
  childData.isBillable = recurringTask?.id ? recurringTask?.is_billable : project.is_billable;

  const subtasksWithIds = useMemo(() => {
    return recurringTask?.subtasks?.map((s) => {
      return { ...s, id: v4() };
    });
  }, [recurringTask?.subtasks]);

  return (
    <ErrorBoundary errorLocation="Recurring Task Form">
      <RecurringTaskForm
        open={open}
        id={id}
        projectId={projectId}
        users={childData.users}
        projectMembers={childData.sortedSubscribers}
        teams={childData.teams}
        companies={childData.companies}
        attachments={childData.attachments}
        taskLists={taskLists}
        canSeeTimeEstimates={childData.canSeeTimeEstimates}
        name={childData.name}
        description={childData.description}
        bodyMode={childData.bodyMode}
        taskListId={childData.taskListId}
        assigneeId={childData?.assigneeId}
        startOn={childData.startOn}
        dueOn={childData.dueOn}
        hiddenFromClients={childData.hiddenFromClients}
        important={childData.important}
        jobTypeId={childData.jobTypeId}
        estimate={childData.estimate}
        subscribers={id ? childData.subscribers : initialProjectSubscriberIds}
        attachUploadedFiles={childData.attachUploadedFiles}
        dropAttachedFiles={childData.dropAttachedFiles}
        labels={childData.labels}
        labelNames={childData.labelNames}
        labelIds={childData.labelIds}
        onClose={onClose}
        hideFromClients={hideFromClients}
        isLoggedUserClient={isLoggedUserClient}
        subtasks={subtasksWithIds}
        repeatAmount={childData.repeatAmount}
        repeatAmountExtended={childData.repeatAmountExtended}
        repeatFrequency={childData.repeatFrequency}
        isBillable={childData.isBillable}
        isTrackingEnabled={project.is_tracking_enabled}
        isProjectNonBillable={project.budget_type === "not_billable"}
      />
    </ErrorBoundary>
  );
};

RecurringTaskFormContainer.propTypes = {
  open: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
};

RecurringTaskFormContainer.defaultchildData = {
  id: 0,
  onClose: () => null,
};

RecurringTaskFormContainer.displayName = "RecurringTaskFormContainer";

export default RecurringTaskFormContainer;
